<template>
  <dx-modal
    :value="show"
    @input="close"
    title="Locais"
    id="schedule-places-modal"
    size="md"
  >
    <div class="places-head">
      <span class="places-eyebrow">Locais de atendimento</span>
      <h5 class="places-title">Onde as agendas atendem</h5>
      <p class="places-hint">
        Escolha o local desta agenda ou cadastre um novo sem sair daqui.
        Locais inativos continuam nas agendas que já usam, mas não aparecem como sugestão.
      </p>
    </div>

    <div class="places-toolbar" v-if="!editing">
      <div class="input-group">
        <span class="input-group-addon"><fa-icon :icon="['fal', 'search']"/></span>
        <input
          ref="search"
          type="text"
          class="form-input"
          placeholder="Buscar local..."
          autocomplete="nope"
          v-model="search"
        >
      </div>
      <button
        class="btn btn-primary btn-icon btn-icon-left"
        v-if="canCreate"
        @click="startCreate"
      ><fa-icon :icon="['fal', 'plus']"/>Novo local</button>
    </div>

    <div class="inline-form" v-if="editing">
      <div class="inline-form-title">{{ form.id ? 'Editar local' : 'Novo local' }}</div>
      <div class="columns">
        <div
          class="column col-sm-12 form-group"
          :class="[form.id ? 'col-8' : 'col-12', { 'has-error': $v.form.name.$error }]"
        >
          <label class="form-label" for="place-name">Nome</label>
          <input
            ref="name"
            id="place-name"
            type="text"
            class="form-input"
            placeholder="Consultório 1 / Sala de exames"
            autocomplete="nope"
            maxlength="100"
            v-model="form.name"
            @blur="$v.form.name.$touch()"
            @keydown.enter.prevent="save"
          >
          <div class="form-input-hint" v-if="$v.form.name.$error && !$v.form.name.required">
            Campo obrigatório
          </div>
        </div>
        <div class="column col-4 col-sm-12 form-group place-active" v-if="form.id">
          <label class="form-switch">
            <input type="checkbox" v-model="form.active">
            <i class="form-icon"/>Ativo
          </label>
        </div>
      </div>
      <div class="inline-form-actions">
        <button class="btn btn-gray" :disabled="saving" @click="cancelEdit">Fechar</button>
        <button
          class="btn btn-secondary btn-icon btn-icon-left"
          :class="{ loading: saving }"
          :disabled="saving"
          @click="save"
        ><fa-icon :icon="['fal', 'save']"/>{{ form.id ? 'Salvar' : 'Cadastrar' }}</button>
      </div>
    </div>

    <div class="loading loading-lg mt-2" v-if="loading"/>

    <div class="places-empty" v-else-if="items.length === 0 && !editing">
      <fa-icon :icon="['fal', 'clinic-medical']" size="2x"/>
      <strong>Nenhum local cadastrado ainda</strong>
      <span v-if="canCreate">Cadastre o primeiro consultório ou sala.</span>
      <span v-else>Peça a quem administra o sistema para cadastrar os locais.</span>
    </div>

    <div class="place-list" v-else-if="!editing">
      <div
        class="place-row"
        :class="{ 'is-selected': item.id === selectedId, 'is-inactive': !item.active }"
        v-for="item in visibleItems"
        :key="item.id"
      >
        <span class="place-icon"><fa-icon :icon="['fal', 'clinic-medical']"/></span>
        <span class="place-text">
          <span class="place-name">{{ item.name }}</span>
          <span class="place-meta">
            <span class="status-pill is-current" v-if="item.id === selectedId">
              <fa-icon :icon="['fal', 'check-circle']"/>Nesta agenda
            </span>
            <span class="status-pill is-inactive" v-if="!item.active">Inativo</span>
          </span>
        </span>
        <span class="place-actions">
          <button
            class="btn btn-sm btn-secondary"
            v-if="item.id !== selectedId && item.active"
            @click="choose(item)"
          >Usar nesta agenda</button>
          <button
            class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
            data-tooltip="Editar"
            v-if="canUpdate"
            @click="startEdit(item)"
          ><fa-icon :icon="['fal', 'pencil']"/></button>
        </span>
      </div>
      <div class="places-none" v-if="visibleItems.length === 0">
        Nenhum local encontrado para “{{ search }}”.
      </div>
    </div>

    <template slot="footer">
      <button class="btn" :disabled="saving" @click="close">Sair</button>
    </template>
  </dx-modal>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { CREATE_PLACE, UPDATE_PLACE } from '@/data/actions/modules/general';
import { resetCache } from '@/helpers/api';

function normalize(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim();
}

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    selectedId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      editing: false,
      search: '',
      items: [],
      form: this.blankForm(),
    };
  },
  validations() {
    return {
      form: {
        name: { required },
      },
    };
  },
  mounted() {
    this.load();
  },
  computed: {
    canCreate() {
      return this.$can(CREATE_PLACE);
    },
    canUpdate() {
      return this.$can(UPDATE_PLACE);
    },
    visibleItems() {
      const query = normalize(this.search);
      if (!query) return this.items;
      return this.items.filter(item => normalize(item.name).includes(query));
    },
  },
  methods: {
    async load() {
      this.loading = true;
      await this.$httpX
        .get('/places', { params: { type: 'clinical', limit: 0 } })
        .then(({ data }) => {
          this.items = data.items;
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    blankForm() {
      return { id: '', name: '', active: true };
    },
    startCreate() {
      if (!this.canCreate) return;
      this.form = { ...this.blankForm(), name: this.search.trim() };
      this.openForm();
    },
    startEdit(item) {
      if (!this.canUpdate) return;
      this.form = { id: item.id, name: item.name, active: item.active };
      this.openForm();
    },
    openForm() {
      this.$v.form.$reset();
      this.editing = true;
      this.$nextTick(() => {
        if (this.$refs.name) this.$refs.name.focus();
      });
    },
    cancelEdit() {
      this.editing = false;
      this.form = this.blankForm();
    },
    async save() {
      this.$v.form.$touch();
      if (this.$v.form.$error || this.saving) return;

      const name = this.form.name.trim();
      const duplicated = this.items.some(item => (
        item.id !== this.form.id && normalize(item.name) === normalize(name)
      ));
      if (duplicated) {
        this.$toast.show('Já existe um local com esse nome', { type: 'error' });
        return;
      }

      this.saving = true;
      const isNew = !this.form.id;
      const request = isNew
        ? this.$httpX.post('/places', { name, type: 'clinical', active: true })
        : this.$httpX.put(`/places/${this.form.id}`, { name, active: this.form.active });

      await request
        .then(async ({ data }) => {
          resetCache();
          this.editing = false;
          this.search = '';
          await this.load();
          this.$emit('changed');
          if (isNew) {
            this.$toast.show('Local cadastrado e selecionado nesta agenda');
            this.choose(data);
          } else {
            this.$toast.show('Local atualizado');
          }
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    choose(item) {
      this.$emit('select', item.id);
      this.close();
    },
    close() {
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/scss/_variables.scss';

#schedule-places-modal {
  .places-head {
    margin-bottom: $layout-spacing-lg;
  }
  .places-eyebrow {
    color: $primary-color;
    display: block;
    font-size: .5rem;
    font-weight: 600;
    letter-spacing: .06em;
    text-transform: uppercase;
  }
  .places-title {
    color: $gray-color-900;
    font-size: .9rem;
    font-weight: 700;
    margin: $layout-spacing-sm 0 0;
  }
  .places-hint {
    color: $gray-color-dark;
    font-size: $font-size-sm;
    margin: $layout-spacing-sm 0 0;
  }
  .places-toolbar {
    align-items: center;
    display: grid;
    gap: $layout-spacing;
    grid-template-columns: minmax(0, 1fr) auto;
    margin-bottom: $layout-spacing;
    .input-group-addon {
      color: $secondary-color;
    }
  }

  .inline-form {
    background-color: #FFF8E6;
    border: $border-width solid #F2C94C;
    border-radius: $border-radius;
    padding: $layout-spacing-lg;
    .inline-form-title {
      color: #8A5A00;
      font-weight: 700;
      margin-bottom: $layout-spacing;
    }
    .place-active {
      align-items: flex-end;
      display: flex;
      .form-switch {
        margin-bottom: .2rem;
      }
    }
    .inline-form-actions {
      display: flex;
      gap: $layout-spacing;
      justify-content: flex-end;
      margin-top: $layout-spacing;
    }
  }

  .place-list {
    background-color: $light-color;
    border: $border-width solid $border-color;
    border-radius: $border-radius;
    box-shadow: 0 1px 2px rgba($dark-color, .06);
    max-height: 20rem;
    overflow: auto;
  }
  .place-row {
    align-items: center;
    border-bottom: $border-width solid $border-color;
    display: grid;
    gap: $layout-spacing;
    grid-template-columns: 1.6rem minmax(0, 1fr) auto;
    padding: $layout-spacing $layout-spacing-lg;
    transition: background-color .15s ease;
    &:last-of-type {
      border-bottom: 0;
    }
    &:hover {
      background-color: $gray-color-ultra-light;
    }
    &.is-selected {
      background-color: lighten($primary-color, 36%);
      .place-icon {
        background-color: $primary-color;
        color: $light-color;
      }
    }
    &.is-inactive {
      .place-name {
        color: $gray-color-dark;
      }
      .place-icon {
        background-color: $gray-color-ultra-light;
        color: $gray-color-dark;
      }
    }
  }
  .place-icon {
    align-items: center;
    background-color: lighten($info-color, 48%);
    border-radius: 50%;
    color: $secondary-color;
    display: flex;
    height: 1.5rem;
    justify-content: center;
    width: 1.5rem;
  }
  .place-text {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: $layout-spacing-sm $layout-spacing;
    min-width: 0;
  }
  .place-name {
    color: $gray-color-900;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .place-meta {
    display: inline-flex;
    gap: $layout-spacing-sm;
  }
  .status-pill {
    align-items: center;
    background-color: $light-color;
    border: $border-width solid $border-color-dark;
    border-radius: 1rem;
    color: $gray-color-dark;
    display: inline-flex;
    font-size: $font-size-xs;
    gap: .2rem;
    line-height: 1;
    padding: .15rem .45rem;
    white-space: nowrap;
    &.is-current {
      border-color: lighten($primary-color, 26%);
      color: $primary-color;
    }
  }
  .place-actions {
    align-items: center;
    display: flex;
    gap: $layout-spacing-sm;
  }
  .places-empty {
    align-items: center;
    background-color: $gray-color-ultra-light;
    border: $border-width dashed $border-color-dark;
    border-radius: $border-radius;
    color: $gray-color-dark;
    display: flex;
    flex-direction: column;
    font-size: $font-size-xs;
    gap: $layout-spacing-sm;
    padding: $layout-spacing-xl $layout-spacing-lg;
    text-align: center;
    svg {
      color: lighten($primary-color, 20%);
      margin-bottom: $layout-spacing-sm;
    }
    strong {
      color: $secondary-color-dark;
      font-size: $font-size-sm;
    }
  }
  .places-none {
    color: $gray-color-dark;
    font-size: $font-size-sm;
    padding: $layout-spacing-lg;
    text-align: center;
  }
}
</style>
